/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/data/GetChineseEnglishSymbolMappings.ts
 * @create:      2020-06-02 15:33:55.173
 * @modify:      2025-07-11 20:47:10.641
 * @version:     6.0.0
 * @times:       112
 * @lines:       94
 * @copyright:   Copyright © 2020-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

/**
 * Array of Chinese and English symbol mappings with descriptions
 * 
 * @returns {Array<{ en: string, zh: string, desc: string }>} 
 *   An array of objects representing English-Chinese symbol mappings.
 * 
 * @since 4.5.1
 * @version 2025-06-12
 */
export function GetChineseEnglishSymbolMappings(): Array<{ en: string; zh: string; desc: string }> {
    return [
        // End punctuation
        { en: ".", zh: "。", desc: "Period (used at the end of a sentence)" },
        { en: "?", zh: "？", desc: "Question Mark (used to indicate a question)" },
        { en: "!", zh: "！", desc: "Exclamation Mark (used to express strong emotion)" },

        // Internal punctuation
        { en: ",", zh: "，", desc: "Comma (used to separate items in a list)" },
        { en: ";", zh: "；", desc: "Semicolon (used to connect independent clauses)" },
        { en: ":", zh: "：", desc: "Colon (used before a list or explanation)" },
        { en: "--", zh: "——", desc: "Dash (used for emphasis or interruption)" },
        { en: "...", zh: "……", desc: "Ellipsis (used to indicate omitted words)" },

        // Brackets and quotation marks
        { en: "(", zh: "（", desc: "Left Parenthesis (used for grouping)" },
        { en: ")", zh: "）", desc: "Right Parenthesis (used for grouping)" },
        { en: "[", zh: "［", desc: "Left Square Bracket (used for lists or annotations)" },
        { en: "]", zh: "］", desc: "Right Square Bracket (used for lists or annotations)" },
        { en: "<", zh: "《", desc: "Left Angle Bracket (used for book titles in Chinese)" },
        { en: ">", zh: "》", desc: "Right Angle Bracket (used for book titles in Chinese)" },
        { en: "\"", zh: "“", desc: "Left Double Quotation Mark (used for direct speech)" },
        { en: "\"", zh: "”", desc: "Right Double Quotation Mark (used for direct speech)" },
        { en: "'", zh: "‘", desc: "Left Single Quotation Mark (used inside double quotes)" },
        { en: "'", zh: "’", desc: "Right Single Quotation Mark (used inside double quotes)" },

        // Special symbols and connectors
        { en: "/", zh: "、", desc: "Slash (used in Chinese as a separator for words)" },
        { en: "~", zh: "～", desc: "Tilde (used to indicate a range)" },
        { en: ".", zh: "·", desc: "Middle Dot (used to separate names in Chinese)" },
        { en: "$", zh: "￥", desc: "Yuan Sign (Chinese currency symbol)" },
        { en: "%", zh: "％", desc: "Percent Sign (used to indicate a percentage)" },
        { en: "&", zh: "＆", desc: "Ampersand (used to mean \"and\")" },

        // Mathematical symbols
        { en: "+", zh: "＋", desc: "Plus Sign (used for addition)" },
        { en: "-", zh: "－", desc: "Minus Sign (used for subtraction)" },
        { en: "*", zh: "×", desc: "Multiplication Sign (used for multiplication)" },
        { en: "/", zh: "÷", desc: "Division Sign (used for division)" },
        { en: "=", zh: "＝", desc: "Equals Sign (used to indicate equality)" },
        { en: "<", zh: "＜", desc: "Less Than Sign (used for comparison)" },
        { en: ">", zh: "＞", desc: "Greater Than Sign (used for comparison)" },

        // Other common symbols
        { en: "@", zh: "＠", desc: "At Sign (used in email addresses)" },
        { en: "#", zh: "＃", desc: "Number Sign (used for hashtags)" },
        { en: "^", zh: "＾", desc: "Caret (used in programming or mathematics)" },
        { en: "_", zh: "＿", desc: "Underscore (used in programming or URLs)" },
        { en: "`", zh: "｀", desc: "Backtick (used in programming for code blocks)" },
        { en: "|", zh: "｜", desc: "Vertical Bar (used for separation or alternation)" },
    ];
}
